import * as React from "react";

import { api } from "@repo/backend";
import { Badge } from "@repo/ui/components/badge";
import { Button } from "@repo/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@repo/ui/components/card";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@repo/ui/components/command";
import { Label } from "@repo/ui/components/label";
import { Popover, PopoverContent, PopoverTrigger } from "@repo/ui/components/popover";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@repo/ui/components/table";
import { Textarea } from "@repo/ui/components/textarea";
import { createFileRoute } from "@tanstack/react-router";
import { useAction, useQuery } from "convex/react";
import { ChevronsUpDown, FileSpreadsheet, LoaderCircle, Upload } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authed/filenames/import")({
  head: () => ({ meta: [{ title: "Import player filenames" }] }),
  component: RouteComponent,
});

type RosterRow = {
  teamName: string;
  teamTag?: string;
  playerName: string;
  playerRole?: string;
  seatingOrder?: number;
  qrPayload?: string;
  isSolo?: boolean;
};

type ImportResult = {
  rowsReceived: number;
  rowsProcessed: number;
  rowsDeduped: number;
  gamesInserted: number;
  teamsInserted: number;
  teamsUpdated: number;
  playersInserted: number;
  playersUpdated: number;
};

function RouteComponent() {
  const games = useQuery(api.gameTitles.list);
  const importRoster = useAction(api.filenameImports.importIpadRoster);
  const [gameId, setGameId] = React.useState<string | null>(null);
  const [fileName, setFileName] = React.useState("");
  const [simpleList, setSimpleList] = React.useState("");
  const [rows, setRows] = React.useState<RosterRow[]>([]);
  const [result, setResult] = React.useState<ImportResult | null>(null);
  const [pending, setPending] = React.useState(false);
  const selectedGame = games?.find((game) => game._id === gameId);

  const chooseFile = async (file?: File) => {
    if (!file) return;
    try {
      const parsed = parseRosterRows(await file.text());
      setRows(parsed);
      setFileName(file.name);
      setResult(null);
      toast.success(`Read ${parsed.length} roster rows.`);
    } catch (error) {
      setRows([]);
      setResult(null);
      toast.error(error instanceof Error ? error.message : "Could not read CSV file.");
    }
  };

  const useSimpleList = () => {
    const parsed = parseSimplePlayerList(simpleList);
    setRows(parsed);
    setFileName("Pasted individual player list");
    setResult(null);
    toast.success(`Read ${parsed.length} individual players.`);
  };

  const submit = async () => {
    if (!selectedGame || !rows.length) return;
    setPending(true);
    try {
      const next = await importRoster({
        rows: rows.map((row) => ({ ...row, gameName: selectedGame.name })),
      });
      setResult(next);
      toast.success(`Imported ${next.rowsProcessed} roster rows.`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not import the iPad roster.");
    } finally {
      setPending(false);
    }
  };

  return (
    <Card className="mx-auto w-full max-w-3xl">
      <CardHeader>
        <CardTitle>Import player filenames</CardTitle>
        <CardDescription>
          Import the roster CSV using the same game, team, and player upsert as the iPad roster
          script.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Game title</Label>
            <GameTitleCombobox
              games={games ?? []}
              value={gameId}
              onValueChange={(value) => {
                setGameId(value);
                setResult(null);
              }}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="filename-csv">Roster CSV</Label>
            <CsvDropzone fileName={fileName} onFile={chooseFile} />
          </div>
          <div className="space-y-2 border-t pt-4">
            <Label htmlFor="simple-player-list">Or paste individual players</Label>
            <Textarea
              id="simple-player-list"
              value={simpleList}
              onChange={(event) => setSimpleList(event.currentTarget.value)}
              placeholder={"Faker\nRuler\nChovy"}
              className="min-h-28 font-mono text-sm"
            />
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs text-muted-foreground">One nickname per line.</p>
              <Button
                type="button"
                variant="outline"
                disabled={!simpleList.trim()}
                onClick={useSimpleList}
              >
                Use player list
              </Button>
            </div>
          </div>
        </div>

        {fileName ? (
          <div className="space-y-4 border-t pt-4">
            <div className="flex flex-wrap items-center gap-3">
              <Badge variant="secondary">{rows.length} rows ready</Badge>
              <Button
                className="ml-auto"
                disabled={!selectedGame || !rows.length || pending}
                onClick={() => void submit()}
              >
                {pending ? <LoaderCircle className="animate-spin" /> : <Upload />}
                Import roster
              </Button>
            </div>

            {result ? (
              <div className="flex flex-wrap gap-2">
                <Badge variant="secondary">{result.playersInserted} players created</Badge>
                <Badge variant="secondary">{result.playersUpdated} players updated</Badge>
                <Badge variant="outline">{result.teamsInserted} teams created</Badge>
                <Badge variant="outline">{result.teamsUpdated} teams updated</Badge>
                {result.rowsDeduped ? (
                  <Badge variant="outline">{result.rowsDeduped} duplicates merged</Badge>
                ) : null}
              </div>
            ) : null}

            <div className="max-h-96 overflow-auto rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Team</TableHead>
                    <TableHead>Team tag</TableHead>
                    <TableHead>Player</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Filename / Unique ID</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row, index) => (
                    <TableRow key={`${row.teamName}-${row.playerName}-${index}`}>
                      <TableCell>{row.teamName}</TableCell>
                      <TableCell>{row.teamTag ?? "—"}</TableCell>
                      <TableCell className="font-medium">{row.playerName}</TableCell>
                      <TableCell>{row.playerRole ?? "—"}</TableCell>
                      <TableCell className="font-mono text-xs">{row.qrPayload ?? "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function GameTitleCombobox({
  games,
  value,
  onValueChange,
}: {
  games: Array<{ _id: string; name: string; tagToken?: string }>;
  value: string | null;
  onValueChange: (value: string) => void;
}) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const selected = games.find((game) => game._id === value);
  const visibleGames = React.useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return games
      .filter(
        (game) =>
          !normalized ||
          game.name.toLowerCase().includes(normalized) ||
          game.tagToken?.toLowerCase().includes(normalized),
      )
      .toSorted((a, b) => a.name.localeCompare(b.name));
  }, [games, query]);

  return (
    <Popover
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) setQuery("");
      }}
    >
      <PopoverTrigger
        render={
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between font-normal"
          />
        }
      >
        <span className={selected ? "truncate" : "truncate text-muted-foreground"}>
          {selected?.name ?? "Select a game"}
        </span>
        <ChevronsUpDown className="opacity-50" />
      </PopoverTrigger>
      {open ? (
        <PopoverContent
          align="start"
          side="bottom"
          className="w-[var(--anchor-width)] min-w-72 p-0"
        >
          <Command shouldFilter={false}>
            <CommandInput
              value={query}
              onValueChange={setQuery}
              placeholder="Search game titles..."
            />
            <CommandList>
              {visibleGames.length ? null : <CommandEmpty>No game titles found.</CommandEmpty>}
              <CommandGroup>
                {visibleGames.map((game) => (
                  <CommandItem
                    key={game._id}
                    value={`${game.name} ${game.tagToken ?? ""}`}
                    data-checked={game._id === value}
                    onSelect={() => {
                      onValueChange(game._id);
                      setOpen(false);
                      setQuery("");
                    }}
                  >
                    <span className="min-w-0 flex-1 truncate">{game.name}</span>
                    {game.tagToken ? (
                      <span className="font-mono text-xs text-muted-foreground">
                        {game.tagToken}
                      </span>
                    ) : null}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      ) : null}
    </Popover>
  );
}

function CsvDropzone({
  fileName,
  onFile,
}: {
  fileName: string;
  onFile: (file?: File) => Promise<void>;
}) {
  const [dragging, setDragging] = React.useState(false);

  return (
    <label
      htmlFor="filename-csv"
      className={`flex min-h-32 cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border border-dashed px-6 py-6 text-center transition-colors ${dragging ? "border-primary bg-primary/5" : "border-input hover:bg-muted/50"}`}
      onDragEnter={(event) => {
        event.preventDefault();
        setDragging(true);
      }}
      onDragOver={(event) => event.preventDefault()}
      onDragLeave={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node)) setDragging(false);
      }}
      onDrop={(event) => {
        event.preventDefault();
        setDragging(false);
        void onFile(event.dataTransfer.files[0]);
      }}
    >
      <input
        id="filename-csv"
        className="sr-only"
        type="file"
        accept=".csv,text/csv"
        onChange={(event) => void onFile(event.target.files?.[0])}
      />
      <FileSpreadsheet className="size-5 shrink-0 text-muted-foreground" />
      <span className="min-w-0 text-sm">
        {fileName ? (
          <>
            <span className="block truncate font-medium">{fileName}</span>
            <span className="block text-muted-foreground">Drop or click to replace</span>
          </>
        ) : (
          <>
            <span className="font-medium">Drop a CSV here</span>
            <span className="block text-muted-foreground">or click to choose a file</span>
          </>
        )}
      </span>
    </label>
  );
}

const clean = (value?: string) => value?.replace(/^\uFEFF/, "").trim() || undefined;
const placeholder = (value: string) => ["-", "#n/a"].includes(value.trim().toLowerCase());
const cleanToken = (value?: string) => {
  const next = clean(value);
  return next && !placeholder(next) && next.toLowerCase() !== "missing" ? next : undefined;
};
const cleanPayload = (value?: string) => {
  const next = clean(value);
  return next && !placeholder(next) ? next : undefined;
};
const normalizeHeader = (value: string) => clean(value)?.toLowerCase().replace(/\s+/g, " ") ?? "";
const filenameToken = (value: string) =>
  value
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");

function parseSimplePlayerList(input: string) {
  return [
    ...input
      .split(/\r?\n/)
      .map((line) => clean(line))
      .filter((name): name is string => name !== undefined && !placeholder(name))
      .reduce((players, playerName) => {
        const token = filenameToken(playerName);
        if (token && !players.has(token)) {
          players.set(token, {
            teamName: playerName,
            teamTag: token,
            playerName,
            playerRole: "Player",
            qrPayload: `_${token}`,
            isSolo: true,
          });
        }
        return players;
      }, new Map<string, RosterRow>())
      .values(),
  ];
}

function parseRosterRows(csv: string) {
  const lines = csv.split(/\r?\n/).map(parseCsvLine);
  const headerIndex = lines.findIndex(
    (cells) =>
      cells.some((cell) => normalizeHeader(cell) === "team name") &&
      cells.some((cell) => normalizeHeader(cell) === "nickname"),
  );
  if (headerIndex < 0) throw new Error("CSV is missing the roster header row.");

  const headers = lines[headerIndex].map(normalizeHeader);
  const find = (names: string[]) =>
    names.map((name) => headers.indexOf(normalizeHeader(name))).find((index) => index >= 0) ?? -1;
  const index = {
    teamName: find(["Team Name"]),
    imagenTeamTag: find(["Imagen Team Name", "Team Short (META)"]),
    temporaryTeamTag: find(["Temporary Imagen Team Name"]),
    gfxTeamTag: find(["GFX Team Ident", "Team Short", "Team Tag"]),
    playerRole: find(["Position", "Role"]),
    seating: find(["Seating Position", "Seating", "Seat"]),
    nickname: find(["Nickname", "Player Nickname", "Player"]),
    uniqueId: find(["Unique ID", "Unique Id"]),
    filenamePlayer: find(["Filename Player", "Base Filename Player"]),
  };
  if ([index.teamName, index.playerRole, index.nickname].some((value) => value < 0)) {
    throw new Error("CSV format does not match the iPad roster import.");
  }

  let previousTeamName: string | undefined;
  let previousTeamTag: string | undefined;
  return lines.slice(headerIndex + 1).reduce<RosterRow[]>((result, cells) => {
    if (cells.every((cell) => !clean(cell))) return result;

    const teamNameCell = clean(cells[index.teamName]);
    const temporaryTeamTag =
      index.temporaryTeamTag >= 0
        ? cleanToken(cells[index.temporaryTeamTag])?.toLowerCase()
        : undefined;
    const teamTagCell = [
      index.imagenTeamTag >= 0 ? cleanToken(cells[index.imagenTeamTag])?.toLowerCase() : undefined,
      temporaryTeamTag,
      index.gfxTeamTag >= 0 ? cleanToken(cells[index.gfxTeamTag])?.toLowerCase() : undefined,
    ].find(Boolean);
    const playerName = clean(cells[index.nickname]);
    const qrPayload =
      cleanPayload(cells[index.uniqueId]) ?? cleanPayload(cells[index.filenamePlayer]);
    const isSolo =
      !teamNameCell &&
      Boolean(
        qrPayload?.startsWith("_") ||
        (playerName && temporaryTeamTag === filenameToken(playerName)),
      );

    if (teamNameCell) {
      previousTeamName = teamNameCell;
      previousTeamTag = teamTagCell;
    }

    const teamName = isSolo ? playerName : (teamNameCell ?? previousTeamName);
    const teamTag =
      isSolo && playerName
        ? filenameToken(playerName)
        : teamNameCell
          ? teamTagCell
          : previousTeamTag;
    if (!teamName || !playerName || placeholder(playerName)) return result;

    const seatingOrder = Number(clean(cells[index.seating]));
    result.push({
      teamName,
      teamTag,
      playerName,
      playerRole: clean(cells[index.playerRole]),
      seatingOrder: index.seating >= 0 && Number.isFinite(seatingOrder) ? seatingOrder : undefined,
      qrPayload,
      isSolo: isSolo || undefined,
    });
    return result;
  }, []);
}

function parseCsvLine(line: string) {
  const cells: string[] = [];
  let current = "";
  let quoted = false;
  for (let index = 0; index < line.length; index += 1) {
    const char = line[index];
    if (char === '"') {
      if (quoted && line[index + 1] === '"') {
        current += '"';
        index += 1;
      } else quoted = !quoted;
    } else if (char === "," && !quoted) {
      cells.push(current);
      current = "";
    } else current += char;
  }
  cells.push(current);
  return cells;
}
